import { Dialog, Button } from '@shared/ui';
import { formatPlanDate } from '../planDate';

interface CancelPlanDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** True while the confirmed mutation (`POST /billing/cancel` or `POST /billing/comp/decline`) is in
   *  flight — both buttons lock, the confirm one says so. The BE answers a double-submit on the comp
   *  path with a 409 rather than a no-op, so this lock is load-bearing, not just polish. */
  pending?: boolean;
  /** ISO instant Pro runs until — `subscription.renewsAt` for an active paid plan, `endsAt` for a
   *  comp (its own `expires_at`, which declining never moves). Null-safe on purpose: see the copy
   *  note below. */
  endsAt: string | null;
}

/**
 * Confirm step for "Switch to Free" (2026-08-16).
 *
 * The whole point is the two facts a user actually wants at that moment, and neither is on the card:
 *
 *  1. **Nothing happens today.** Pro runs to the end of the period they already paid for — the
 *     user's own rule, *"if the user is downgrading to free from pro, it will happen on the next
 *     cycle of subscription"*. Cancelling is not a refund request and must not read like one.
 *  2. **Nothing is deleted.** Data-loss fear is the real objection here — a trading journal is years
 *     of work — and the free tier keeps all of it. Saying so is cheaper than any retention offer.
 *
 * The undo line is stated up front rather than discovered later: the user's cancel-then-regret case
 * is a supported flow ("Keep Pro" on the Pro card), so the dialog says so before they commit.
 *
 * With an unknown date (`endsAt` null — shouldn't happen for `proActive`, but the wire allows it) the
 * copy degrades to "the end of your current billing period" instead of rendering a broken date. It
 * never invents one.
 *
 * **Serves BOTH downgrade paths, deliberately un-forked (2026-08-16).** A comped user declining their
 * comp gets this exact dialog, because every sentence in it is already true of a comp: Pro runs to
 * the date it always had (declining moves nothing — the comp's `expires_at` is untouched), nothing is
 * deleted, and the undo is the same "Keep Pro" button on the same card. Only the endpoint behind
 * `onConfirm` differs (`/billing/comp/decline` vs `/billing/cancel`), and that is the caller's
 * business, not this component's — so there is no `variant` prop, and above all no second, hand-copied
 * dialog: a duplicated shell is exactly what produced the "the plans components are not equal in
 * size" drift. The one comp-inapplicable string, the "current billing period" fallback above, is
 * unreachable on that path — `canDeclineComp` is false for an undated comp, so a comp that can reach
 * this dialog always has a date.
 */
export function CancelPlanDialog({ open, onClose, onConfirm, pending = false, endsAt }: CancelPlanDialogProps) {
  const on = formatPlanDate(endsAt);
  const until = on !== null ? on : 'the end of your current billing period';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Switch to Free?"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Stay on Pro
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={pending}>
            {pending ? 'Switching…' : 'Switch to Free'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm leading-6 text-text">
        <p>
          You&rsquo;ll keep Pro until <span className="font-semibold">{until}</span>. After that
          you&rsquo;re on Free. Your journal, trades, imports and history all stay.
        </p>
        <p className="text-text-muted">
          You can undo this any time before {until} with <span className="font-semibold">Keep Pro</span>.
        </p>
      </div>
    </Dialog>
  );
}
